/**
 * Featured Carousel card media — same hover-to-play + sound icon as Recent Content
 * (`CreatorFeedVideoPlayer` / TikTok tiles). Content tiles cover-fill 9:16 so
 * vertical social posts show their full frame; product tiles use native-ratio
 * contain on a light frame (no LiB product bank).
 *
 * Video players are deferred until the tile is near the viewport (or hovered) so
 * homepage peek strips don't mount multiple players during first paint.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { CreatorFeedVideoPlayer } from "../../../components/FeedPost/PlatformPosts/CreatorFeedVideoPlayer";
import { preserveBrokenImageBox } from "../../../Utils/imageUtils";
import { getBudgetedTemplateImageSrc } from "../../../Utils/templateImageDelivery";
import { NoMediaPost } from "../LandingPageCarousels/CommonComponents/NoMediaPost";
import type { FeaturedCarouselItem } from "./types";
import { textPlatformNoMediaLabel } from "./contentLooks";
import { TemplatePostMediaCarousel } from "./TemplatePostMediaCarousel";

/** Pictures already decoded this session. Add remounts the same thumbs;
 *  starting them unloaded paints the black skeleton for a frame. */
const loadedStillSrcs = new Set<string>();

export function rememberLoadedStill(src: string) {
  const s = String(src || "").trim();
  if (!s || s.endsWith("/assets/dummy_video.png")) return;
  loadedStillSrcs.add(s);
}

export function stillAlreadyLoaded(src: string) {
  return loadedStillSrcs.has(String(src || "").trim());
}

type Props = {
  item: Pick<
    FeaturedCarouselItem,
    | "image"
    | "mediaUrl"
    | "mediaUrls"
    | "thumbnailUrls"
    | "isVideo"
    | "title"
    | "postType"
    | "productImage"
    | "isText"
    | "platform"
    | "postDate"
  >;
  /** Extra class on the root media wrapper (e.g. marquee). */
  className?: string;
  /** Hover-play audio. Defaults to on (same as live Featured Carousel). */
  soundOn?: boolean;
  /** Above-the-fold tiles — eager fetch so strip thumbs are not deferred. */
  priority?: boolean;
  /**
   * Marquee visual duplicates (aria-hidden) — lazy so they don't double the
   * image budget on Slow 4G / 3G. Visible strip tiles stay eager.
   */
  deferLoad?: boolean;
  /**
   * Force poster/still (no hover video). Use for CSS marquees — duplicated
   * video players stall transform animations on iOS.
   */
  stillOnly?: boolean;
  /** Content = cover 9:16; product = contain on light frame. */
  variant?: "content" | "product";
  /** Product tiles: report natural media aspect once the image loads. */
  onMediaAspectRatio?: (ratio: number) => void;
};

export function FeaturedCarouselCardMedia({
  item,
  className,
  soundOn = true,
  priority = false,
  deferLoad = false,
  stillOnly = false,
  variant = "content",
  onMediaAspectRatio,
}: Props) {
  const isProduct = variant === "product";
  const isText = Boolean(item.isText) && !isProduct;
  const platform = String(item.platform || "").trim().toLowerCase();
  const stillCandidates = useMemo(() => {
    const out: string[] = [];
    const push = (raw: unknown) => {
      const s = String(raw || "").trim();
      if (s && !out.includes(s)) out.push(s);
    };
    if (isProduct) push(item.productImage);
    push(item.image);
    return out;
  }, [isProduct, item.productImage, item.image]);
  const [srcIndex, setSrcIndex] = useState(0);
  useEffect(() => {
    setSrcIndex(0);
  }, [stillCandidates.join("\0")]);
  const rawThumb =
    stillCandidates[srcIndex] ||
    (isText ? "" : "/assets/dummy_video.png");
  // Budgeted WebP at explicit 9:16 cover (width-only render breaks these thumbs).
  const thumb = rawThumb ? getBudgetedTemplateImageSrc(rawThumb, "fc") : "";
  const videoSrc = String(item.mediaUrl || "").trim();
  const slides =
    !isProduct && Array.isArray(item.mediaUrls) && item.mediaUrls.length > 1
      ? item.mediaUrls
      : [];
  const isCarousel = slides.length > 1;
  const wantsVideo = Boolean(
    !stillOnly &&
      !isProduct &&
      !isText &&
      item.isVideo &&
      videoSrc &&
      // Podcast episode art is always a still — never mute / hover-play.
      platform !== "podcast",
  );

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [allowVideo, setAllowVideo] = useState(false);
  /** Parent hover — keeps play + mute overlay after deferred player mount. */
  const [cardHover, setCardHover] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(() =>
    stillAlreadyLoaded(thumb),
  );
  // Match legacy LiB: mute control only when Media Sound is on (not YouTube).
  const muteEnabled = Boolean(soundOn) && platform !== "youtube";

  const onCardEnter = () => {
    setAllowVideo(true);
    setCardHover(true);
  };
  const onCardLeave = () => setCardHover(false);

  useEffect(() => {
    // Empty src never fires onLoad — without this, Group tiles with no cover
    // stay on the skeleton forever ("preview broken").
    if (!String(thumb || "").trim() || stillAlreadyLoaded(thumb)) {
      setImageLoaded(true);
      return;
    }
    setImageLoaded(false);
  }, [thumb]);

  useEffect(() => {
    const markIfReady = () => {
      const img = imgRef.current;
      if (!(img?.complete && img.naturalWidth > 0)) return;
      rememberLoadedStill(String(img.currentSrc || img.src || thumb || ""));
      setImageLoaded(true);
      if (onMediaAspectRatio && img.naturalHeight > 0) {
        onMediaAspectRatio(img.naturalWidth / img.naturalHeight);
      }
    };
    markIfReady();
    const img = imgRef.current;
    if (!img) return;
    img.addEventListener("load", markIfReady);
    // Add to page remounts the same thumb. A cached image can finish
    // without firing `load`, which left this skeleton up.
    const raf = window.requestAnimationFrame(markIfReady);
    return () => {
      window.cancelAnimationFrame(raf);
      img.removeEventListener("load", markIfReady);
    };
    // Intentionally only re-check when thumb changes — parent callbacks are unstable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thumb]);

  useEffect(() => {
    if (!wantsVideo && (!isCarousel || stillOnly)) return;
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setAllowVideo(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setAllowVideo(true);
          io.disconnect();
        }
      },
      { rootMargin: "120px 0px", threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [wantsVideo, isCarousel, stillOnly, videoSrc]);

  if (isText) {
    return (
      <div
        className={[
          "mte-tpl-fc-media",
          "is-text-platform",
          "is-loaded",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <NoMediaPost
          post={{
            platform: item.platform || platform,
            caption: item.title,
            post_date: item.postDate,
          }}
          type={platform || "content"}
          label={textPlatformNoMediaLabel(platform)}
          // square-image: opt out of LiB feed 143×250; parent FC card sizes the tile.
          className="newsletter-media-carousel no-media-url square-image mte-tpl-fc-nomedia-card"
          showDate={Boolean(item.postDate)}
          showMeta={false}
        />
      </div>
    );
  }

  // No usable thumb — black type card (Group members without a cover).
  if (!String(thumb || "").trim()) {
    return (
      <div
        className={[
          "mte-tpl-fc-media",
          "is-text-platform",
          "is-loaded",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <NoMediaPost
          post={{
            platform: item.platform || platform,
            caption: item.title,
            post_date: item.postDate,
          }}
          type={platform || "content"}
          label={textPlatformNoMediaLabel(platform)}
          className="newsletter-media-carousel no-media-url square-image mte-tpl-fc-nomedia-card"
          showDate={Boolean(item.postDate)}
          showMeta={false}
        />
      </div>
    );
  }

  const revealed = imageLoaded || stillAlreadyLoaded(thumb);

  const wrapClass = [
    "mte-tpl-fc-media",
    isProduct ? "is-product" : "",
    isCarousel || revealed ? "is-loaded" : "is-loading",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const playVideo = wantsVideo && allowVideo && !isCarousel;

  const markLoaded = (img: HTMLImageElement) => {
    rememberLoadedStill(String(img.currentSrc || img.src || thumb || ""));
    setImageLoaded(true);
    if (
      onMediaAspectRatio &&
      img.naturalWidth > 0 &&
      img.naturalHeight > 0
    ) {
      onMediaAspectRatio(img.naturalWidth / img.naturalHeight);
    }
  };

  if (isCarousel) {
    return (
      <div
        ref={wrapRef}
        className={wrapClass}
        onMouseEnter={onCardEnter}
        onMouseLeave={onCardLeave}
        onFocus={onCardEnter}
        onBlur={onCardLeave}
      >
        <TemplatePostMediaCarousel
          slides={slides}
          posters={item.thumbnailUrls}
          fallbackPoster={thumb}
          stillOnly={stillOnly}
          allowVideo={allowVideo}
          isSoundOn={muteEnabled}
          focused={cardHover}
          playableVideoUrl={videoSrc}
          imageClassName="mte-tpl-img-filter mte-tpl-fc-img is-loaded"
          videoClassName="creator-feed-card-video hover-play mte-tpl-fc-hover-video"
        />
      </div>
    );
  }

  if (playVideo) {
    return (
      <div
        ref={wrapRef}
        className={wrapClass}
        onMouseEnter={onCardEnter}
        onMouseLeave={onCardLeave}
        onFocus={onCardEnter}
        onBlur={onCardLeave}
      >
        <CreatorFeedVideoPlayer
          media_url={videoSrc}
          thumbnail={thumb}
          isSoundOn={muteEnabled}
          focused={cardHover}
          className="creator-feed-card-video hover-play mte-tpl-fc-hover-video"
        />
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      className={wrapClass}
      onMouseEnter={() => {
        if (wantsVideo) onCardEnter();
      }}
      onMouseLeave={onCardLeave}
      onFocus={() => {
        if (wantsVideo) onCardEnter();
      }}
      onBlur={onCardLeave}
    >
      {!revealed ? (
        <div
          className="mte-tpl-skel-fill mte-tpl-skel-fill--cover mte-tpl-fc-media-skel"
          aria-hidden
        />
      ) : null}
      <img
        ref={imgRef}
        src={thumb}
        alt=""
        className={`mte-tpl-img-filter mte-tpl-fc-img${isProduct ? " mte-tpl-fc-img--contain" : ""}${revealed ? " is-loaded" : ""}`}
        // Visible strip tiles stay eager (scroll-snap / aspect). Marquee
        // duplicates use lazy so they don't contend on Slow 4G / 3G.
        loading={deferLoad ? "lazy" : "eager"}
        decoding="async"
        {...(priority && !deferLoad
          ? ({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)
          : {})}
        onLoad={(e) => markLoaded(e.currentTarget)}
        onError={(event) => {
          if (srcIndex + 1 < stillCandidates.length) {
            setSrcIndex((i) => i + 1);
            return;
          }
          preserveBrokenImageBox(event);
        }}
      />
    </div>
  );
}
